<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { campanhaPost } from '../helper/campanhaApi';

const nome = ref('');
const template = ref('Oi {{nome}}, temos uma novidade pra você!');
const fLabel = ref('');
const fValor = ref('');
const fDias = ref('');
const throttle = ref(30);
const tag = '{{nome}}';
const msg = ref('');
const msgKind = ref('');
const saving = ref(false);
const camps = ref([]);
const erro = ref('');
let timer = null;

const setMsg = (t, k) => { msg.value = t; msgKind.value = k || ''; };

const montarFiltro = () => {
  const f = {};
  if (fLabel.value && fLabel.value.trim()) f.label = fLabel.value.trim();
  const v = parseFloat(fValor.value);
  if (!Number.isNaN(v)) f.valor_min = v;
  const d = parseInt(fDias.value, 10);
  if (!Number.isNaN(d)) f.sem_contato_dias = d;
  return f;
};

const carregar = async () => {
  try {
    const r = await campanhaPost('campanha-listar', {});
    camps.value = Array.isArray(r) ? r : [];
    erro.value = '';
  } catch (e) { erro.value = String((e && e.message) || e); }
};

const preview = async () => {
  setMsg('Calculando público...', '');
  try {
    const j = await campanhaPost('campanha-preview', { filtro: montarFiltro() });
    if (j && j.ok) setMsg('Público: ' + j.total + ' contato(s) com esse filtro.', 'ok');
    else setMsg('Não foi possível prever o público.', 'err');
  } catch (e) { setMsg('Erro: ' + ((e && e.message) || e), 'err'); }
};

const criar = async () => {
  if (!nome.value.trim()) { setMsg('Informe o nome da campanha.', 'err'); return; }
  if (!template.value.trim()) { setMsg('Informe a mensagem.', 'err'); return; }
  saving.value = true; setMsg('Criando...', '');
  try {
    const j = await campanhaPost('campanha-criar', {
      nome: nome.value.trim(),
      template_texto: template.value,
      throttle_seg: parseInt(throttle.value, 10) || 30,
      filtro: montarFiltro(),
    });
    if (j && j.ok) {
      setMsg('Campanha criada com ' + j.recipients + ' destinatário(s). Clique em "Iniciar" na lista.', 'ok');
      nome.value = '';
      await carregar();
    } else setMsg('Não foi possível criar a campanha.', 'err');
  } catch (e) { setMsg('Erro: ' + ((e && e.message) || e), 'err'); } finally { saving.value = false; }
};

const acao = async (id, ac) => {
  try { await campanhaPost('campanha-status', { id, acao: ac }); await carregar(); }
  catch (e) { erro.value = String((e && e.message) || e); }
};

const pct = (n, total) => {
  const t = Math.max(Number(total) || 0, 1);
  return ((Number(n) || 0) / t) * 100;
};
const badgeClass = (s) => ({
  draft: 'bg-n-slate-4 text-n-slate-11',
  running: 'bg-n-teal-4 text-n-teal-11',
  paused: 'bg-n-amber-4 text-n-amber-11',
  done: 'bg-n-slate-3 text-n-slate-10',
}[s] || 'bg-n-slate-4 text-n-slate-11');

onMounted(() => { carregar(); timer = setInterval(carregar, 15000); });
onUnmounted(() => { if (timer) clearInterval(timer); });
</script>

<template>
  <div class="flex flex-col h-full bg-n-background text-n-slate-12 p-4 overflow-y-auto">
    <h1 class="text-xl font-semibold mb-1">Campanhas</h1>
    <p class="text-sm text-n-slate-11 mb-4 max-w-2xl">Disparo em massa com template, filtro de público e throttle. Use <code>{{ tag }}</code> no texto para o primeiro nome do contato.</p>
    <div v-if="erro" class="text-n-ruby-11 text-sm mb-3">Erro: {{ erro }}</div>

    <div class="bg-n-solid-2 rounded-xl outline outline-1 outline-n-container p-4 mb-4 max-w-3xl flex flex-col gap-3">
      <div class="text-sm font-semibold text-n-slate-12">Nova campanha</div>
      <input v-model="nome" placeholder="Nome da campanha (ex: Promo de Junho)" class="w-full bg-n-alpha-black2 rounded-lg px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" />
      <textarea v-model="template" rows="3" placeholder="Texto enviado ao contato (personalizável)" class="w-full bg-n-alpha-black2 rounded-lg px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand resize-y"></textarea>
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label class="block text-xs text-n-slate-11 mb-1">Label/origem do lead</label>
          <input v-model="fLabel" placeholder="ex: campanha-teste" class="w-full bg-n-alpha-black2 rounded-lg px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" />
        </div>
        <div>
          <label class="block text-xs text-n-slate-11 mb-1">Valor mínimo (R$)</label>
          <input v-model="fValor" type="number" placeholder="0" class="w-full bg-n-alpha-black2 rounded-lg px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" />
        </div>
        <div>
          <label class="block text-xs text-n-slate-11 mb-1">Sem contato há (dias)</label>
          <input v-model="fDias" type="number" placeholder="" class="w-full bg-n-alpha-black2 rounded-lg px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" />
        </div>
        <div>
          <label class="block text-xs text-n-slate-11 mb-1">Throttle (s)</label>
          <input v-model="throttle" type="number" class="w-full bg-n-alpha-black2 rounded-lg px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" />
        </div>
      </div>
      <div class="flex gap-2 items-center">
        <button class="text-sm px-3 py-2 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="preview">Prever público</button>
        <button :disabled="saving" class="text-sm px-4 py-2 rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-40" @click="criar">{{ saving ? 'Criando...' : 'Criar campanha' }}</button>
        <span v-if="msg" class="text-xs" :class="msgKind === 'ok' ? 'text-n-teal-11' : (msgKind === 'err' ? 'text-n-ruby-11' : 'text-n-slate-11')">{{ msg }}</span>
      </div>
    </div>

    <div class="bg-n-solid-2 rounded-xl outline outline-1 outline-n-container p-4 max-w-3xl">
      <div class="flex items-center justify-between mb-2">
        <div class="text-sm font-semibold text-n-slate-12">Campanhas ({{ camps.length }})</div>
        <button class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="carregar">Atualizar</button>
      </div>
      <div class="flex flex-col gap-3">
        <div v-for="c in camps" :key="c.id" class="bg-n-solid-1 rounded-lg p-3">
          <div class="flex items-center justify-between gap-2">
            <div class="text-sm font-semibold text-n-slate-12">{{ c.nome }}</div>
            <span class="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" :class="badgeClass(c.status)">{{ c.status }}</span>
          </div>
          <div class="h-2 bg-n-alpha-black2 rounded-full overflow-hidden flex my-2">
            <div class="h-full bg-n-brand" :style="{ width: pct(c.sent, c.total) + '%' }"></div>
            <div class="h-full bg-n-teal-9" :style="{ width: pct(c.replied, c.total) + '%' }"></div>
          </div>
          <div class="text-[11px] text-n-slate-11 flex gap-3 flex-wrap">
            <span>Total {{ c.total || 0 }}</span><span>Pendentes {{ c.pending || 0 }}</span><span>Enviados {{ c.sent || 0 }}</span><span>Responderam {{ c.replied || 0 }}</span><span v-if="c.failed">Falhas {{ c.failed }}</span>
          </div>
          <div class="mt-2">
            <button v-if="c.status === 'running'" class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="acao(c.id, 'pausar')">Pausar</button>
            <button v-else-if="c.status === 'draft' || c.status === 'paused'" class="text-xs px-3 py-1.5 rounded-lg bg-n-brand text-white hover:opacity-90" @click="acao(c.id, 'retomar')">{{ c.status === 'draft' ? 'Iniciar' : 'Retomar' }}</button>
          </div>
        </div>
        <div v-if="!camps.length" class="text-xs text-n-slate-10">Nenhuma campanha ainda.</div>
      </div>
    </div>
  </div>
</template>
